import { useCallback, useMemo } from 'react';
import { IoMdArrowDropupCircle } from 'react-icons/io';
import { useLocation } from 'react-router-dom';

import { TemplatePlayer } from '../../types/team';
import Clubs from '../Clubs';
import { IconPlayerIncidents } from '../IconPlayerIncidents';
import { IconPlayerStatus } from '../IconPlayerStatus';
import { ShieldClub } from '../Shield';
import Tags from '../Tags';

import { Button } from '@/components';
import useGetClubColorById from '@/hooks/useGetClubColorById';
import { useIncidents } from '@/hooks/useIncidents';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { useTournament } from '@/hooks/useTournament';
import { TeamActionTypes } from '@/store';
import { KeysOf } from '@/types';
import {
  EquipoZona,
  VallasInvictasClub,
  MyPlayersScore,
  Goleadores,
  LeyDelEx,
  JugNoJuegan,
  JugTarjetometro,
  BuenosYBaratos,
  JugFigura,
  ListaRepo,
  Mas,
} from '@/types/fieldAssistance';
import {
  EMPTY_TEMPLATE_PLAYER,
  MATCH_STATUS,
  PLAYER_POSITION_BY_ID,
  cn,
  formatMillionBalance,
  hashNameComponents,
} from '@/utils';

type PositionKeys = KeysOf<typeof PLAYER_POSITION_BY_ID>;

export type VariantsAssistance = {
  variant?:
    | 'DEFAULT'
    | 'GOL'
    | 'INVICTUS'
    | 'EX'
    | 'ARE_OUT'
    | 'SCORE_LAST_MATCH'
    | 'TARJETOMETRO'
    | 'EMBED';
};

interface PropsFixture extends VariantsAssistance {
  idLocal: number | string;
  idVisitor: number | string;
  localClub: string;
  visitorClub: string;
  dateMatch?: string;
  golsLocal?: number | undefined | string;
  golsVisitor?: number | undefined | string;
  onClick?: () => void;
  txtVerDetallePartido?: string | React.ReactNode;
  zona?: string;
  isInterzonal?: boolean;
  hideZones?: boolean;
  idEstado: number | undefined;
}

const ItemFixture = ({
  idLocal,
  idVisitor,
  localClub,
  visitorClub,
  dateMatch,
  golsLocal,
  golsVisitor,
  variant = 'DEFAULT',
  onClick,
  txtVerDetallePartido,
  zona,
  isInterzonal,
  hideZones,
  idEstado,
}: PropsFixture) => {
  const { isDesktop } = useIsDesktop();

  const { primary: colorLocalPrimary, secondary: colorLocalSecond } =
    useGetClubColorById(+idLocal);

  const { primary: colorVisitorPrimary, secondary: colorVisitorSecond } =
    useGetClubColorById(+idVisitor);

  const zonas: Record<'desktop' | 'mobile', Record<number, string>> = {
    desktop: {
      1: 'ZONA A',
      2: 'ZONA B',
    },
    mobile: {
      1: 'ZA',
      2: 'ZB',
    },
  };

  const obtenerZona = (zona?: string) => {
    const zonaNum = Number(zona);
    return isDesktop
      ? zonas.desktop[zonaNum] ?? 'CLÁSICO'
      : zonas.mobile[zonaNum] ?? 'CL';
  };

  return (
    <>
      <div
        className={cn(
          'flex h-[70px] items-center border-b-[1px] border-b-gdt-gray-7 bg-white space-x-3 lg:space-x-7 text-xs lg:text-sm',
          {
            'pl-12': variant === 'SCORE_LAST_MATCH',
          }
        )}
      >
        <div className="flex-1 font-bold flex items-center justify-end">
          <p className="mr-1 lg:mr-2">{localClub || '-'}</p>
          <div className="bg-gdt-gray-7 h-7 w-7 flex items-center justify-center rounded">
            <figure className="w-4">
              <ShieldClub
                key={idLocal}
                endColor={colorLocalPrimary || ''}
                startColor={colorLocalSecond || ''}
              />
            </figure>
          </div>
        </div>
        {golsLocal && golsVisitor && idEstado !== MATCH_STATUS.SUSPENDIDO ? (
          <p className="text-gdt-status-green text-sm lg:text-base bg-white px-2 py-[2px] rounded shadow-sm min-w-[3.3rem] lg:min-w-[77px] text-center flex items-center justify-center">
            <strong>{golsLocal}</strong>
            &nbsp;-&nbsp;
            <strong>{golsVisitor}</strong>
          </p>
        ) : (
          <strong className="text-gdt-status-green text-sm lg:text-base bg-white px-2 py-[2px] rounded shadow-sm min-w-[3.3rem] lg:min-w-[77px] text-center">
            {dateMatch || '-'}
          </strong>
        )}
        <div className="flex-1 font-bold flex items-center justify-between pr-4">
          <div className="flex items-center">
            <div className="bg-gdt-gray-7 h-7 w-7 flex items-center justify-center rounded">
              <figure className="w-4">
                <ShieldClub
                  key={idVisitor}
                  endColor={colorVisitorPrimary || ''}
                  startColor={colorVisitorSecond || ''}
                />
              </figure>
            </div>
            <p className="ml-1 lg:ml-2">{visitorClub || '-'}</p>
          </div>

          {/* Zona alineada a la derecha */}
          {variant !== 'SCORE_LAST_MATCH' && (
            <p className="text-gray-500 font-light">
              {/* Dos excepciones:  
              1) hideZones: (fecha de playoffs), no existe zona
              2) isInterzonal: En la fecha "interzonal" no se muestra info de zona, porque todos dirían "clásico"
              */}
              {!hideZones && !isInterzonal ? obtenerZona(zona) : ''}
            </p>
          )}
        </div>
        {variant === 'SCORE_LAST_MATCH' && onClick && isDesktop && (
          <div>
            {idEstado !== MATCH_STATUS.SUSPENDIDO ? (
              <Button
                className="underline-offset-8 font-medium mr-8"
                type="button"
                variant="text"
                onClick={() => {
                  onClick();
                }}
              >
                {txtVerDetallePartido || 'Ver detalle del partido'}
              </Button>
            ) : (
              <p className="font-medium mr-8">
                Partido postergado&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

interface DefaultTypes extends VariantsAssistance {
  templatePlayer: TemplatePlayer;
  dataPlayerFieldAssistance?: Goleadores | LeyDelEx | JugNoJuegan;
}

const ItemDefault = ({
  templatePlayer,
  dataPlayerFieldAssistance,
  variant = 'DEFAULT',
}: DefaultTypes) => {
  const { pathname } = useLocation();
  const { isMobile } = useIsDesktop();

  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  const getGoalsPenal = useCallback((gol: number | null): string => {
    if (gol && gol > 0) {
      return ` (${gol} de penal)`;
    }
    return '';
  }, []);

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray">
            {templatePlayer.a}, {templatePlayer.n}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-1 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            {(variant === 'GOL' || variant === 'EX') && (
              <Tags.Alert statusId={templatePlayer.e}>
                <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
              </Tags.Alert>
            )}
          </div>
        </div>
      </div>
      {(variant === 'GOL' || variant === 'EX') && (
        <div className="text-right space-y-1 lg:flex lg:items-center lg:space-y-0">
          {/* TEXTO GOLEADORES */}
          {variant === 'GOL' && templatePlayer.gol !== null && (
            <p className="text-xs lg:text-sm font-bold text-gdt-gray-2 max-w-[165px]">
              {templatePlayer?.gol > 1
                ? `${templatePlayer.gol} goles ${getGoalsPenal(
                    templatePlayer.golPen
                  )}`
                : `${templatePlayer.gol} gol ${getGoalsPenal(
                    templatePlayer.golPen
                  )}`}
            </p>
          )}
          {/* TEXTO LEY DEL EX */}
          {variant === 'EX' && (
            <p className="text-xs lg:text-sm font-bold text-gdt-gray-2 max-w-[165px]">
              Jugó en{' '}
              {dataPlayerFieldAssistance &&
                'exClub' in dataPlayerFieldAssistance &&
                dataPlayerFieldAssistance.exClub}
            </p>
          )}
          <Button
            className="underline-offset-8 text-xs lg:text-sm font-bold lg:ml-5"
            href={
              ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
            }
            type="link"
            variant="text"
            onClick={() => {
              dispatch({
                type: TeamActionTypes.SET_STATE,
                payload: {
                  selectedPlayerOnList: templatePlayer,
                  ...(ifEmbedded
                    ? { playerDetailsModalOpen: true }
                    : { showPlayersListTransfer: true }),
                },
              });
            }}
          >
            Agregar
          </Button>
        </div>
      )}
      {variant === 'DEFAULT' && (
        <div className="lg:pr-10">
          <Tags.Alert statusId={templatePlayer.e}>
            <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
          </Tags.Alert>
        </div>
      )}
    </div>
  );
};

interface TarjetometroTypes extends VariantsAssistance {
  templatePlayer: TemplatePlayer;
  dataPlayerFieldAssistance?: JugTarjetometro;
}

const ItemTarjetometro = ({
  templatePlayer,
  dataPlayerFieldAssistance,
}: TarjetometroTypes) => {
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray">
            {templatePlayer.a}, {templatePlayer.n}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-2 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right flex items-center space-x-3 lg:space-x-1">
        <div className="text-center bg-gdt-status-yellow-light min-w-8 h-10 px-1 items-center content-center rounded-sm">
          <p className="text-base font-bold text-gdt-status-yellow">
            {dataPlayerFieldAssistance?.amarillas ?? 0}
          </p>
        </div>
        <div className="text-center bg-gdt-status-red-light min-w-8 h-10 px-1 items-center content-center rounded-sm">
          <p className="text-base font-bold text-gdt-status-red">
            {dataPlayerFieldAssistance?.rojas ?? 0}
          </p>
        </div>
      </div>
    </div>
  );
};

const ItemDefault2 = ({ player }: { player: MyPlayersScore }) => {
  const { isMobile } = useIsDesktop();
  const { pathname } = useLocation();

  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const playerByTemplate = usePlayerTemplateMap();
  const templatePlayer =
    playerByTemplate.get(player.id) || EMPTY_TEMPLATE_PLAYER;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm bg-white">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray">
            {templatePlayer.a}, {templatePlayer.n}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-1 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right flex items-start w-[100px] lg:max-w-[244px] lg:w-full font-bold text-xs text-gdt-gray-2">
        <p className="flex-1 text-center lg:text-center">{player.ptosGDT}</p>
        <p className="flex-1 lg:text-center">
          {player.ptosClarin}
          <Button
            className="underline-offset-8 text-xs lg:text-sm font-bold mt-1"
            href={
              ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
            }
            type="link"
            variant="text"
            onClick={() => {
              dispatch({
                type: TeamActionTypes.SET_STATE,
                payload: {
                  selectedPlayerOnList: templatePlayer,
                  ...(ifEmbedded
                    ? { playerDetailsModalOpen: true }
                    : { showPlayersListTransfer: true }),
                },
              });
            }}
          >
            Agregar
          </Button>
        </p>
      </div>
    </div>
  );
};

interface PropsItemIncidents extends VariantsAssistance {
  player: MyPlayersScore;
}
const ItemIncidents = ({ player, variant }: PropsItemIncidents) => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { pathname } = useLocation();

  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap?.get(player?.clubId || -1);
  const { incidentsDesktop, incidentsMobile } = useIncidents(player);
  const { dispatch, state } = useTeamStore();
  const playerByTemplate = usePlayerTemplateMap();

  const templatePlayer = useMemo(() => {
    if (variant === 'SCORE_LAST_MATCH') {
      return playerByTemplate.get(player.id) || EMPTY_TEMPLATE_PLAYER;
    }
  }, [player.id, playerByTemplate, variant]);

  const randomNumber = Math.floor(Math.random() * 1000);

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  return (
    <div className="px-4 lg:px-[0] lg:pl-7 flex lg:h-[63px] h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3">
        <figure className="w-[28px] h-[31px] relative top-1">
          <Clubs clubId={club?.id} playerId={player.id} type={'SHIRT'} />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray flex">
            {player.nombre}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {player.posicionCancha} - {player.club}
          </p>
          {isMobile && incidentsMobile && (
            <div className="w-full text-3xs lg:text-sm font-semibold text-gdt-blue uppercase lg:hidden flex">
              {incidentsMobile.join(', ')}
            </div>
          )}

          {isDesktop && incidentsDesktop && (
            <div className="items-center gap-2 hidden lg:flex">
              {incidentsDesktop.map((data, index) => {
                return (
                  <IconPlayerIncidents
                    key={index + randomNumber}
                    id={index + randomNumber}
                    text={data.text}
                    textTooltip={data.textTooltip && data.textTooltip}
                    variant={data.variant}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div
        className={cn(
          'text-right flex items-center w-[100px] lg:max-w-[244px] lg:w-full font-bold text-sm text-gdt-gray',
          {
            'lg:max-w-[344px]': variant === 'SCORE_LAST_MATCH',
          }
        )}
      >
        <p className="flex-1 text-center lg:text-center">
          {player.isJugo ? player.ptosGDT : '-'}
        </p>
        <p className="flex-1 lg:text-center">
          {player.isJugo ? player.ptosClarin : '-'}
        </p>
        {variant === 'SCORE_LAST_MATCH' && (
          <Button
            className="underline-offset-8 text-xs lg:text-sm font-bold mt-1 mr-6"
            href={
              ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
            }
            type="link"
            variant="text"
            onClick={() => {
              dispatch({
                type: TeamActionTypes.SET_STATE,
                payload: {
                  selectedPlayerOnList: templatePlayer,
                  ...(ifEmbedded
                    ? { playerDetailsModalOpen: true }
                    : { showPlayersListTransfer: true }),
                },
              });
            }}
          >
            Agregar
          </Button>
        )}
      </div>
    </div>
  );
};

interface PropsInvictus extends VariantsAssistance {
  team: VallasInvictasClub;
  index: number;
}

const ItemInvictus = ({ team, index }: PropsInvictus) => {
  const { isDesktop } = useIsDesktop();
  const { primary, secondary } = useGetClubColorById(team.id);

  return (
    <div className="flex items-center border-b-[1px] border-b-gdt-gray-6 h-[60px] lg:h-[73px] pr-5 lg:pr-[75px]">
      {isDesktop && (
        <div className="text-center lg:w-[70px] w-12 hidden lg:block">
          <div
            className={cn(
              'bg-gdt-blue-xl2 font-bold text-sm text-gdt-blue h-8 w-8 flex items-center justify-center rounded-full ml-[10px] lg:ml-11',
              {
                'bg-gdt-blue text-white': index === 0,
              }
            )}
          >
            {team.orden}
          </div>
        </div>
      )}
      <div className="text-left font-bold text-xs lg:text-sm text-gdt-gray flex-1 lg:ml-1 flex items-center">
        <figure className="bg-gdt-gray-7 w-[30px] rounded-md p-2 ml-4 mr-5 lg:mr-2">
          <ShieldClub key={team.id} endColor={secondary} startColor={primary} />
        </figure>
        <p>{team?.nombreClub}</p>
      </div>
      <div className="text-center font-bold text-xs lg:text-sm text-gdt-gray-3 lg:text-gdt-gray flex-1 lg:mr-10 flex items-center lg:justify-center justify-end">
        {team.vallasInvictas > 1
          ? `${team.vallasInvictas} vallas invictas`
          : `${team.vallasInvictas} valla invicta`}
      </div>
    </div>
  );
};

interface PropsPositions extends VariantsAssistance {
  team: EquipoZona;
}

const ItemPositions = ({ team }: PropsPositions) => {
  const { isDesktop } = useIsDesktop();
  const { primary, secondary } = useGetClubColorById(team.club.id);

  return (
    <div
      className={cn(
        'flex items-center border-b-[1px] border-b-gdt-gray-6 h-[60px] lg:h-[53px] pr-5 lg:pr-[75px]'
      )}
    >
      <div className="text-center lg:w-[70px] w-12">
        <div
          className={cn(
            'bg-gdt-blue-xl2 font-bold text-sm text-gdt-blue h-8 w-8 flex items-center justify-center rounded-full ml-[10px] lg:ml-11',
            {
              'bg-gdt-blue text-white': team?.nroLinea <= 8,
            }
          )}
        >
          {team?.nroLinea}
        </div>
      </div>
      <div className="text-left font-bold text-xs lg:text-sm text-gdt-gray flex-1 ml-1 flex items-center">
        {isDesktop && (
          <figure className="w-4 bg-gdt-gray-7 lg:w-[30px] rounded-md p-2 lg:ml-4 lg:mr-2 hidden lg:block">
            <ShieldClub
              key={team.club.id}
              endColor={secondary}
              startColor={primary}
            />
          </figure>
        )}
        <p>{team?.club.nombre}</p>
      </div>
      <div className="flex items-center justify-between text-xs text-gdt-gray-3 lg:font-bold lg:text-sm lg:max-w-[560px]">
        <div className="text-center w-9 lg:w-16 font-bold text-gdt-gray lg:text-gdt-gray-3">
          {team?.puntaje || '-'}
        </div>
        <div className="text-center w-6 lg:w-16">
          {team?.partidosJugados || '-'}
        </div>
        <div className="text-center w-6 lg:w-16">{team?.ganados || '-'}</div>
        <div className="text-center w-6 lg:w-16">{team?.empatados || '-'}</div>
        <div className="text-center w-6 lg:w-16">{team?.perdidos || '-'}</div>
        <div className="text-center w-6 lg:w-16">
          {team?.golesAFavor || '-'}
        </div>
        <div className="text-center w-6 lg:w-16">
          {team?.golesEnContra || '-'}
        </div>
      </div>
    </div>
  );
};

interface BuenosYBaratosTypes extends VariantsAssistance {
  templatePlayer: TemplatePlayer;
  dataPlayerFieldAssistance?: BuenosYBaratos;
}

const ItemBuenosYBaratos = ({
  templatePlayer,
  dataPlayerFieldAssistance,
}: BuenosYBaratosTypes) => {
  const { pathname } = useLocation();
  const { isMobile } = useIsDesktop();

  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  const promedio = dataPlayerFieldAssistance?.promedioUltimasTresFechas || 0;

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray">
            {templatePlayer.a}, {templatePlayer.n}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-1 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right space-y-1 lg:flex lg:items-center lg:space-y-0">
        <p className="text-xs lg:text-sm font-bold text-gdt-gray-2 max-w-[165px]">
          {Number.isInteger(promedio)
            ? promedio
            : promedio.toFixed(2).replace('.', ',')}{' '}
          Puntos
        </p>
        <Button
          className="underline-offset-8 text-xs lg:text-sm font-bold lg:ml-5"
          href={
            ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
          }
          type="link"
          variant="text"
          onClick={() => {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                selectedPlayerOnList: templatePlayer,
                ...(ifEmbedded
                  ? { playerDetailsModalOpen: true }
                  : { showPlayersListTransfer: true }),
              },
            });
          }}
        >
          Agregar
        </Button>
      </div>
    </div>
  );
};

interface TopPlayersTypes extends VariantsAssistance {
  templatePlayer: TemplatePlayer;
  dataPlayerFieldAssistance?: JugFigura;
}

const ItemTopPlayers = ({
  templatePlayer,
  dataPlayerFieldAssistance,
}: TopPlayersTypes) => {
  const { pathname } = useLocation();
  const { isMobile } = useIsDesktop();

  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  const figuraCount = dataPlayerFieldAssistance?.valorReporte || 0;
  const figuraText =
    figuraCount === 1
      ? `Figura ${figuraCount} vez`
      : `Figura ${figuraCount} veces`;

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray">
            {templatePlayer.a}, {templatePlayer.n}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-1 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right space-y-1 lg:flex lg:items-center lg:space-y-0">
        <p className="text-xs lg:text-sm font-bold text-gdt-gray-2 max-w-[165px]">
          {figuraText}
        </p>
        <Button
          className="underline-offset-8 text-xs lg:text-sm font-bold lg:ml-5"
          href={
            ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
          }
          type="link"
          variant="text"
          onClick={() => {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                selectedPlayerOnList: templatePlayer,
                ...(ifEmbedded
                  ? { playerDetailsModalOpen: true }
                  : { showPlayersListTransfer: true }),
              },
            });
          }}
        >
          Agregar
        </Button>
      </div>
    </div>
  );
};

interface IdealTeamTypes extends VariantsAssistance {
  templatePlayer: TemplatePlayer;
  dataPlayerFieldAssistance?: ListaRepo;
}

const ItemIdealTeam = ({
  templatePlayer,
  dataPlayerFieldAssistance,
}: IdealTeamTypes) => {
  const { pathname } = useLocation();
  const { isMobile } = useIsDesktop();

  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  const points = dataPlayerFieldAssistance?.ptsTotal || 0;

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="relative w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <div className="flex mr-5 space-x-1 iot">
            <p className="text-xs lg:text-base font-bold text-gdt-gray ">
              {templatePlayer.a}, {templatePlayer.n}
            </p>
            {dataPlayerFieldAssistance?.capitan && (
              <IconPlayerStatus variant="capitan" />
            )}
          </div>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-1 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right space-y-1 lg:flex lg:items-center lg:space-y-0">
        <p className="text-xs lg:text-sm font-bold text-gdt-gray-2 max-w-[165px]">
          {points} puntos
        </p>
        <Button
          className="underline-offset-8 text-xs lg:text-sm font-bold lg:ml-5"
          href={
            ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
          }
          type="link"
          variant="text"
          onClick={() => {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                selectedPlayerOnList: templatePlayer,
                ...(ifEmbedded
                  ? { playerDetailsModalOpen: true }
                  : { showPlayersListTransfer: true }),
              },
            });
          }}
        >
          Agregar
        </Button>
      </div>
    </div>
  );
};

interface ReplacementsTypes extends VariantsAssistance {
  templatePlayer: TemplatePlayer;
  dataPlayerFieldAssistance?: Mas;
  type: 'ENTRADAS' | 'SALIDAS';
}

const ItemReplacements = ({
  templatePlayer,
  dataPlayerFieldAssistance,
  type,
}: ReplacementsTypes) => {
  const { isMobile } = useIsDesktop();

  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  const count =
    type === 'ENTRADAS'
      ? dataPlayerFieldAssistance?.entradas || 0
      : dataPlayerFieldAssistance?.salidas || 0;

  const label = type === 'ENTRADAS' ? 'Entradas' : 'Salidas';

  return (
    <div className="px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm bg-white">
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex  lg:items-center">
          <span className="text-xs lg:text-base font-bold text-gdt-gray whitespace-nowrap">
            {templatePlayer.a}, {templatePlayer.n}
          </span>
          <span className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase whitespace-nowrap">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as PositionKeys]} -{' '}
            {club?.nombre}
          </span>
          <div className="flex items-center justify-normal space-x-3 ">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3 whitespace-nowrap">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right flex items-center flex-col md:flex-row justify-end w-[100px] lg:max-w-[244px] lg:w-full font-bold text-xs text-gdt-gray-2">
        <div
          className={cn(
            'flex items-center justify-end gap-1 text-xs lg:text-sm font-bold lg:mr-4',
            type === 'ENTRADAS' ? 'text-gdt-green' : 'text-gdt-danger'
          )}
        >
          <IoMdArrowDropupCircle
            className={cn(
              'text-base lg:text-lg',
              type === 'ENTRADAS'
                ? 'text-gdt-status-green'
                : 'text-gdt-status-red rotate-180'
            )}
          />
          <p>
            {label}:
            <span
              className={cn(
                type === 'ENTRADAS'
                  ? 'text-gdt-status-green'
                  : 'text-gdt-status-red '
              )}
            >
              {' '}
              {count}
            </span>
          </p>
        </div>
        {type === 'ENTRADAS' && (
          <Button
            className="underline-offset-8 text-xs lg:text-sm font-bold text-gdt-blue hover:underline p-0 h-auto"
            type="button"
            variant="text"
            onClick={() => {
              dispatch({
                type: TeamActionTypes.SET_STATE,
                payload: {
                  selectedPlayerOnList: templatePlayer,
                  ...(ifEmbedded
                    ? { playerDetailsModalOpen: true }
                    : { showPlayersListTransfer: true }),
                },
              });
            }}
          >
            Agregar
          </Button>
        )}
      </div>
    </div>
  );
};

export const ItemAssistant = {
  Fixture: ItemFixture,
  Default: ItemDefault,
  Default2: ItemDefault2,
  Incidents: ItemIncidents,
  Positions: ItemPositions,
  Invictus: ItemInvictus,
  Tarjetometro: ItemTarjetometro,
  BuenosYBaratos: ItemBuenosYBaratos,
  TopPlayers: ItemTopPlayers,
  IdealTeam: ItemIdealTeam,
  Replacements: ItemReplacements,
};
